import type { MailAccount, MailMessage, MailThread } from "@/lib/api";
import {
  formatMailAddressListFull,
  formatMailContact,
  formatMailFromFull,
  mailContactEmails,
} from "@/mail/formatContact";
import { plainToHtml, stripHtml } from "@/mail/htmlBody";
import { readMailClockFormat, type MailClockFormatId } from "@/mail/prefs";
import { insertMailSignature, readMailSignature } from "@/mail/signature";

export type ComposeMode = "new" | "reply" | "replyAll" | "forward";

export type ComposeAttachment = {
  filename: string;
  mimeType: string;
  contentBase64: string;
  size?: number;
  /** Set when Hub compressed the photo for mail size limits. */
  compressedFromSize?: number;
  /** Content-ID for multipart/related inline images (Tip 193). */
  contentId?: string;
  /** When true, MIME uses Content-Disposition: inline (shown in body). */
  inline?: boolean;
};

/** Saved To/Cc for reply ↔ reply-all toggle without reloading the source message. */
export type ComposeModeRecipients = {
  to: string;
  cc: string;
};

export type ComposeDraft = {
  mode: ComposeMode;
  accountId: string;
  to: string;
  cc: string;
  bcc: string;
  subject: string;
  body: string;
  threadId?: string;
  inReplyTo?: string;
  references?: string;
  showCc: boolean;
  showBcc: boolean;
  attachments: ComposeAttachment[];
  /** Message being replied to / edited (draft). */
  sourceMessageId?: string;
  /** Mailbox the source thread belongs to — used to drop foreign threadIds on From switch. */
  sourceAccountId?: string;
  /**
   * Stable Hub list/detail key for the open conversation at compose time.
   * Survives cross-account replies that clear `threadId` so send can still
   * paint into the same chain.
   */
  sourceThreadKey?: string;
  /**
   * Tip 129: presets so the in-compose Reply / Reply all buttons can rewrite
   * To/Cc (previously only flipped `mode` and looked broken).
   */
  modePresets?: {
    reply: ComposeModeRecipients;
    replyAll: ComposeModeRecipients;
  };
  /** When updating a provider draft (Gmail draft id). */
  providerDraftId?: string;
};

function pickEmail(value: unknown): string {
  if (value == null) return "";
  if (typeof value === "string") {
    const trimmed = value.trim();
    const angle = trimmed.match(/<([^>]+@[^>]+)>/);
    if (angle) return angle[1].trim().toLowerCase();
    if (trimmed.includes("@")) return trimmed.toLowerCase();
    return "";
  }
  if (Array.isArray(value)) {
    return value.map(pickEmail).filter(Boolean).join(", ");
  }
  if (typeof value === "object") {
    const record = value as Record<string, unknown>;
    if (record.emailAddress && typeof record.emailAddress === "object") {
      const nested = pickEmail(record.emailAddress);
      if (nested) return nested;
    }
    const email = pickEmail(record.email ?? record.Email ?? record.address ?? record.Address);
    if (email) return email;
  }
  return "";
}

function splitEmails(value: unknown): string[] {
  return mailContactEmails(value);
}

function toEmailsOf(message: MailMessage): string[] {
  return message.toEmails?.length ? message.toEmails : splitEmails(message.to);
}

function ccEmailsOf(message: MailMessage): string[] {
  return message.ccEmails?.length ? message.ccEmails : splitEmails(message.cc);
}

/** Coerced messages can hold From as a display name only; recover it from thread participants. */
function participantEmailForName(thread: MailThread, name: string): string {
  const wanted = name.trim().toLowerCase();
  const participants = (thread as MailThread & { participants?: unknown }).participants;
  if (!wanted || !Array.isArray(participants)) return "";
  for (const entry of participants) {
    if (!entry || typeof entry !== "object") continue;
    const record = entry as Record<string, unknown>;
    const entryName = formatMailContact(record.name ?? record.displayName).toLowerCase();
    if (entryName && entryName === wanted) {
      const email = splitEmails(entry)[0];
      if (email) return email;
    }
  }
  return "";
}

function uniqueEmails(emails: string[], exclude: Set<string>): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const email of emails) {
    const key = email.toLowerCase();
    if (!key || exclude.has(key) || seen.has(key)) continue;
    seen.add(key);
    out.push(email);
  }
  return out;
}

function htmlHasBlockBreaks(html: string): boolean {
  return /<(?:p|div|br|li|blockquote|h[1-6]|tr|pre)\b/i.test(html);
}

function messageBodyText(message: MailMessage): string {
  const plain = message.bodyText?.replace(/\r\n/g, "\n").trim() || "";
  const html = message.bodyHtml?.trim() || "";
  // A list row often only has a one-line preview. Quote the HTML when it still has paragraphs.
  if (html && htmlHasBlockBreaks(html) && !plain.includes("\n")) return stripHtml(html);
  if (plain) return plain;
  if (html) return stripHtml(html);
  return message.snippet || "";
}

function fromEmailOf(message: MailMessage): string {
  return (
    pickEmail(message.fromEmail) ||
    pickEmail(message.from) ||
    pickEmail(message.fromName) ||
    ""
  );
}

function fromDisplayOf(message: MailMessage): string {
  return formatMailContact(message.fromName) || formatMailContact(message.from) || fromEmailOf(message);
}

function messageIdHeader(message: MailMessage): string | undefined {
  const record = message as MailMessage & { messageIdHeader?: string; messageId?: string };
  return record.messageIdHeader || record.messageId || undefined;
}

/** Parse ISO / epoch / alternate provider date fields into a Date. */
function coerceMessageDate(message: MailMessage, thread?: MailThread): Date | null {
  const raw = message as MailMessage & Record<string, unknown>;
  const threadRaw = (thread || {}) as MailThread & Record<string, unknown>;
  const candidates: unknown[] = [
    message.date,
    raw.at,
    raw.receivedAt,
    raw.internalDate,
    raw.sentAt,
    thread?.date,
    threadRaw.at,
    threadRaw.receivedAt,
    threadRaw.internalDate,
  ];
  for (const value of candidates) {
    if (value == null || value === "") continue;
    if (value instanceof Date && !Number.isNaN(value.getTime())) return value;
    if (typeof value === "number" && Number.isFinite(value)) {
      const ms = value < 1e12 ? value * 1000 : value;
      const date = new Date(ms);
      if (!Number.isNaN(date.getTime())) return date;
      continue;
    }
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (!trimmed) continue;
      if (/^\d+$/.test(trimmed)) {
        const asNum = Number(trimmed);
        const ms = asNum < 1e12 ? asNum * 1000 : asNum;
        const date = new Date(ms);
        if (!Number.isNaN(date.getTime())) return date;
        continue;
      }
      const date = new Date(trimmed);
      if (!Number.isNaN(date.getTime())) return date;
    }
  }
  return null;
}

/**
 * Gmail-style quote attribution:
 *   On Sat, Sep 26, 2026 at 5:02 PM Name <email> wrote:
 *   On Sat, Sep 26, 2026 at 5:02 PM <email> wrote:
 */
function formatGmailQuoteWho(message: MailMessage): string {
  const email = fromEmailOf(message);
  const name =
    formatMailContact(message.fromName) ||
    (formatMailContact(message.from).includes("@") ? "" : formatMailContact(message.from));
  if (name && email && name.toLowerCase() !== email.toLowerCase()) {
    return `${name} <${email}>`;
  }
  if (email) return `<${email}>`;
  return name || "Unknown";
}

function formatGmailQuoteWhen(date: Date, clock: MailClockFormatId): string {
  // Match Gmail’s en-US quote header (weekday, month day, year at time).
  const weekday = date.toLocaleDateString("en-US", { weekday: "short" });
  const monthDay = date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  const year = date.getFullYear();
  const time = date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: clock !== "24h",
  });
  return `${weekday}, ${monthDay}, ${year} at ${time}`;
}

function quoteAddressLine(display: unknown, emails?: string[]): string {
  const full = formatMailAddressListFull(display);
  if (full.includes("@")) return full;
  if (emails?.length) return emails.join(", ");
  return full;
}

/**
 * Header Gmail keeps with a quoted message: who sent it, when, who was on it,
 * and the subject. Each field is its own line so a reply chain stays readable.
 */
function quoteHeaderLines(
  message: MailMessage,
  thread: MailThread | undefined,
  clock: MailClockFormatId,
): string[] {
  const from = formatMailFromFull(message);
  const date = coerceMessageDate(message, thread);
  const when = date ? formatGmailQuoteWhen(date, clock) : "";
  const to = quoteAddressLine(message.to, message.toEmails);
  const cc = quoteAddressLine(message.cc, message.ccEmails);
  const subject = (message.subject || thread?.subject || "").trim();
  return [
    from ? `From: ${from}` : "",
    when ? `Date: ${when}` : "",
    to ? `To: ${to}` : "",
    cc ? `Cc: ${cc}` : "",
    subject ? `Subject: ${subject}` : "",
  ].filter(Boolean);
}

function formatGmailQuoteAttribution(
  message: MailMessage,
  thread?: MailThread,
  clock: MailClockFormatId = readMailClockFormat(),
): string {
  const who = formatGmailQuoteWho(message);
  const date = coerceMessageDate(message, thread);
  if (!date) return `On ${who} wrote:`;
  return `On ${formatGmailQuoteWhen(date, clock)} ${who} wrote:`;
}

export function isDraftMessage(message: MailMessage, _folder?: string): boolean {
  if (message.isDraft) return true;
  const labels = message.labelIds || [];
  if (labels.some((label) => String(label).toUpperCase().includes("DRAFT"))) return true;
  return false;
}

export function emptyComposeDraft(accountId: string): ComposeDraft {
  return {
    mode: "new",
    accountId,
    to: "",
    cc: "",
    bcc: "",
    subject: "",
    body: insertMailSignature("", readMailSignature(accountId, "new")),
    showCc: false,
    showBcc: false,
    attachments: [],
  };
}

export function buildComposeDraft(opts: {
  mode: ComposeMode;
  thread: MailThread;
  message: MailMessage;
  account: MailAccount | undefined;
}): ComposeDraft {
  const { mode, thread, message, account } = opts;
  const accountId = account?.id || thread.accountId || message.accountId || "";
  const accountEmail = (account?.email || "").trim().toLowerCase();
  const exclude = new Set(accountEmail ? [accountEmail] : []);
  const subjectBase = thread.subject || message.subject || "";
  const mid = messageIdHeader(message);

  if (mode === "forward") {
    const alreadyFwd = subjectBase.toLowerCase().startsWith("fwd:");
    const subject = alreadyFwd ? subjectBase : `Fwd: ${subjectBase}`;
    const forwardedAt = coerceMessageDate(message, thread);
    const when = forwardedAt
      ? formatGmailQuoteWhen(forwardedAt, readMailClockFormat())
      : "";
    const email = fromEmailOf(message);
    const name = fromDisplayOf(message);
    const fromLine =
      name && email && name.toLowerCase() !== email.toLowerCase()
        ? `From: ${name} <${email}>`
        : email
          ? `From: ${email}`
          : `From: ${name}`;
    const body = [
      "",
      "---------- Forwarded message ----------",
      fromLine,
      when ? `Date: ${when}` : "",
      `Subject: ${subjectBase}`,
      `To: ${formatMailContact(message.to) || pickEmail(message.to)}`,
      "",
      messageBodyText(message),
    ]
      .filter((line, index, arr) => !(line === "" && arr[index - 1] === ""))
      .join("\n");

    return {
      mode,
      accountId,
      to: "",
      cc: "",
      bcc: "",
      subject,
      body: insertMailSignature(plainToHtml(body), readMailSignature(accountId, "reply")),
      showCc: false,
      showBcc: false,
      attachments: [],
      sourceMessageId: message.id,
    };
  }

  const modePresets = buildReplyModePresets(message, thread, exclude);
  const active = mode === "replyAll" ? modePresets.replyAll : modePresets.reply;
  const ccList = splitEmails(active.cc);

  const alreadyRe = subjectBase.toLowerCase().startsWith("re:");
  const subject = alreadyRe ? subjectBase : `Re: ${subjectBase}`;
  const clock = readMailClockFormat();
  const header = quoteHeaderLines(message, thread, clock);
  const quoted = [...header, "", ...messageBodyText(message).split("\n")]
    .map((line) => (line.length ? `> ${line}` : ">"))
    .join("\n");
  // Gmail-style: blank lines for typing, then attribution, then the quoted
  // message with its From / Date / To / Cc / Subject lines.
  const attribution = formatGmailQuoteAttribution(message, thread, clock);
  const body = `\n\n${attribution}\n${quoted}`;

  const sourceAccountId = thread.accountId || message.accountId || accountId;
  // Foreign Gmail thread ids (reply From another mailbox) create a new chain —
  // keep In-Reply-To / References so the provider can still thread by headers.
  const sameMailbox = Boolean(accountId) && accountId === sourceAccountId;
  // Match MailPage.threadRowKey so send can find the open conversation.
  const sourceThreadKey = `${thread.accountId || "acct"}::${String(thread.threadId ?? thread.id ?? "")}`;
  return {
    mode,
    accountId,
    to: active.to,
    cc: active.cc,
    bcc: "",
    subject,
    body: insertMailSignature(plainToHtml(body), readMailSignature(accountId, "reply")),
    threadId: sameMailbox ? thread.threadId || thread.id : undefined,
    inReplyTo: mid,
    references: mid,
    showCc: ccList.length > 0,
    showBcc: false,
    attachments: [],
    sourceMessageId: message.id,
    sourceAccountId,
    sourceThreadKey,
    modePresets,
  };
}

/** Compute Reply vs Reply-all To/Cc sets (self excluded). */
export function buildReplyModePresets(
  message: MailMessage,
  thread: MailThread,
  exclude: Set<string>,
): { reply: ComposeModeRecipients; replyAll: ComposeModeRecipients } {
  const from =
    fromEmailOf(message) ||
    participantEmailForName(thread, formatMailContact(message.fromName) || formatMailContact(message.from));
  const replyToHeader = splitEmails(
    (message as MailMessage & { replyTo?: unknown }).replyTo,
  );
  const toList = toEmailsOf(message);
  const ccList = ccEmailsOf(message);
  let replyTo = uniqueEmails([...replyToHeader, from], exclude);
  let replyAllTo = uniqueEmails([...replyToHeader, from, ...toList], exclude);
  // Own-sent message: Reply goes back to whoever it was sent To (Gmail behavior),
  // never to the Cc list or the rest of the thread.
  const ownSent = Boolean(from) && exclude.has(from.toLowerCase());
  if (replyTo.length === 0 && ownSent) replyTo = uniqueEmails(toList, exclude);
  // Sender unknown: last resort is a single address, so Reply never turns into Reply all.
  if (replyTo.length === 0) {
    const participants = (thread as MailThread & { participants?: unknown }).participants;
    const fallback = uniqueEmails(
      [
        ...toList,
        ...ccList,
        ...(Array.isArray(participants)
          ? participants.flatMap((entry) => splitEmails(entry))
          : []),
      ],
      exclude,
    );
    replyTo = fallback.slice(0, 1);
  }
  if (replyAllTo.length === 0) replyAllTo = replyTo;
  const replyAllCc = uniqueEmails(
    ccList,
    new Set([...exclude, ...replyAllTo.map((email) => email.toLowerCase())]),
  );
  return {
    reply: { to: replyTo.join(", "), cc: "" },
    replyAll: { to: replyAllTo.join(", "), cc: replyAllCc.join(", ") },
  };
}

/** Apply Reply / Reply-all presets onto an open draft (keeps body/subject/attachments). */
export function applyReplyMode(
  draft: ComposeDraft,
  next: "reply" | "replyAll",
): ComposeDraft {
  const presets = draft.modePresets;
  if (!presets) return { ...draft, mode: next };
  const set = presets[next];
  return {
    ...draft,
    mode: next,
    to: set.to,
    cc: set.cc,
    showCc: Boolean(set.cc.trim()) || (next === "replyAll" && draft.showCc),
  };
}

export function draftFromExistingMessage(opts: {
  thread: MailThread;
  message: MailMessage;
  account: MailAccount | undefined;
}): ComposeDraft {
  const { thread, message, account } = opts;
  const html = message.bodyHtml?.trim() || plainToHtml(messageBodyText(message));
  const accountId = account?.id || thread.accountId || message.accountId || "";
  return {
    mode: "new",
    accountId,
    to: toEmailsOf(message).join(", ") || formatMailContact(message.to),
    cc: ccEmailsOf(message).join(", ") || formatMailContact(message.cc),
    bcc: "",
    subject: message.subject || thread.subject || "",
    body: html,
    threadId: thread.threadId || thread.id,
    showCc: Boolean(formatMailContact(message.cc)),
    showBcc: false,
    // Bytes are filled by MailPage.openEditDraft via hydrateDraftAttachments.
    attachments: [],
    sourceMessageId: message.id,
    sourceAccountId: thread.accountId || message.accountId || accountId,
    sourceThreadKey: `${thread.accountId || "acct"}::${String(thread.threadId ?? thread.id ?? "")}`,
  };
}
